import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

interface SearchResult {
  title: string;
  description: string;
  url: string;
  category: string;
  tags: string[];
}

const searchIndex: SearchResult[] = [
  // Solutions
  { title: "Surveillance & Security Solutions", description: "Body-worn cameras and mission-critical communications from Hytera.", url: "/solutions/surveillance", category: "Solutions", tags: ["body camera", "bwv", "surveillance", "security", "hytera", "law enforcement"] },
  { title: "Rugged Computing Solutions", description: "MIL-SPEC laptops, tablets, and handheld computers from Honeywell and Dell Rugged Pro.", url: "/solutions/rugged-computing", category: "Solutions", tags: ["rugged", "tablet", "laptop", "honeywell", "dell", "mil-spec", "warehouse", "field", "handheld"] },
  { title: "HCI & Storage Infrastructure", description: "Replace VMware and Nutanix with Arcfra — complete hyperconverged infrastructure and data protection.", url: "/solutions/data-protection", category: "Solutions", tags: ["hci", "storage", "arcfra", "exagrid", "synology", "vmware", "nutanix", "backup", "data protection", "hyperconverged"] },
  { title: "Solutions Overview", description: "Explore all SE Distribution solutions across surveillance, rugged computing, and infrastructure.", url: "/solutions", category: "Solutions", tags: ["solutions", "overview"] },
  // Products — Hytera BWV
  { title: "Hytera SC880 5G Body Camera", description: "5G flagship body camera — 4K video, 300s pre-record, 10-hr battery, IP68, MIL-STD-810G.", url: "/hytera-bwv", category: "Products", tags: ["sc880", "5g", "body camera", "hytera", "4k", "bwv", "law enforcement"] },
  { title: "Hytera SC780 4G Body Camera", description: "4G LTE smart body camera — 1080P, 130° FOV, 13-hr battery, 120s pre-record, IP68.", url: "/hytera-bwv", category: "Products", tags: ["sc780", "4g", "body camera", "hytera", "bwv"] },
  { title: "Hytera GC550 Body Camera", description: "Compact body camera — 1080P, 8-hr battery, IP67, infrared night vision, AES-256.", url: "/hytera-bwv", category: "Products", tags: ["gc550", "compact camera", "hytera", "bwv", "night vision"] },
  { title: "Hytera DEMS Evidence Platform", description: "Digital evidence management — chain-of-custody, AI search, audit trail, cloud upload.", url: "/hytera-bwv", category: "Products", tags: ["dems", "evidence", "hytera", "digital evidence", "chain of custody"] },
  { title: "Hytera BWC Spotlight", description: "Hytera body-worn camera lineup — SC880 5G, SC780 4G, GC550 & DEMS evidence platform.", url: "/hytera-bwv", category: "Spotlight", tags: ["hytera", "bwc", "body worn camera", "spotlight", "body camera"] },
  // Products — Rugged
  { title: "Dell Pro Rugged 10 Tablet", description: "World's lightest 10\" fully-rugged Windows tablet — 1.04kg, IP66, Intel Core Ultra 200V, Wi-Fi 7.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "tablet", "10 inch", "windows", "mil-spec"] },
  { title: "Dell Pro Rugged 12 Tablet", description: "12\" fully-rugged tablet — 1200-nit, dual hot-swap batteries, Intel Core Ultra 200V, Wi-Fi 7.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "tablet", "12 inch", "windows"] },
  { title: "Dell Pro Rugged 13 Laptop", description: "World's smallest 13\" fully-rugged AI PC — 1400-nit, IP65, 6ft drop-tested, optional 5G.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "laptop", "13 inch", "mil-spec"] },
  { title: "Dell Pro Rugged 14 Laptop", description: "14\" semi-rugged AI PC — optional NVIDIA RTX 500 Ada, 1100-nit, IP53.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "laptop", "14 inch"] },
  { title: "Honeywell ScanPal EDA10A", description: "10-inch Android enterprise tablet — 5G, Wi-Fi 6, IP65, MIL-STD-810H, 8000mAh.", url: "/rugged-spotlight", category: "Products", tags: ["honeywell", "scanpal", "eda10a", "tablet", "android", "rugged"] },
  { title: "Honeywell RT10 Android Tablet", description: "10-inch Android rugged tablet — FlexRange scanner, hot-swap battery, 4G LTE CAT13.", url: "/rugged-spotlight", category: "Products", tags: ["honeywell", "rt10", "android", "tablet", "rugged", "flexrange"] },
  { title: "Honeywell RT10 Windows Tablet", description: "10-inch Windows 10 Enterprise LTSC rugged tablet — FlexRange scanner, vehicle dock ready.", url: "/rugged-spotlight", category: "Products", tags: ["honeywell", "rt10", "windows", "tablet", "rugged"] },
  { title: "Rugged Computing Spotlight", description: "Full Honeywell and Dell Rugged Pro device lineup — compare specs and find the right device.", url: "/rugged-spotlight", category: "Spotlight", tags: ["rugged", "spotlight", "honeywell", "dell", "tablet", "laptop"] },
  // Products — Storage
  { title: "Arcfra ArcHCI Platform", description: "VMware and Nutanix alternative — full HCI stack, software-defined, scale-out architecture.", url: "/arcfra-spotlight", category: "Products", tags: ["arcfra", "hci", "hyperconverged", "vmware alternative", "nutanix alternative"] },
  { title: "Arcfra ArcStor All-Flash Array", description: "NVMe-oF all-flash storage — sub-100µs latency, HA dual controller.", url: "/arcfra-spotlight", category: "Products", tags: ["arcfra", "all-flash", "nvme", "storage", "array"] },
  { title: "ExaGrid EX Series Backup Appliance", description: "Scale-out backup appliances — 3TB to 3PB, tiered landing zone, ransomware protection.", url: "/exagrid-spotlight", category: "Products", tags: ["exagrid", "backup", "appliance", "ransomware", "dedup"] },
  { title: "Synology DiskStation NAS", description: "Enterprise NAS — 8-bay, 10GbE, PCIe expansion, DiskStation Manager.", url: "/solutions/data-protection", category: "Products", tags: ["synology", "nas", "diskstation", "storage"] },
  { title: "Arcfra Platform Spotlight", description: "Deep-dive into Arcfra HCI — compare vs VMware and Nutanix, see full product lineup.", url: "/arcfra-spotlight", category: "Spotlight", tags: ["arcfra", "spotlight", "hci", "vmware", "nutanix"] },
  { title: "ExaGrid Backup Spotlight", description: "ExaGrid backup storage — tiered landing zone, instant VM recovery, ransomware shield.", url: "/exagrid-spotlight", category: "Spotlight", tags: ["exagrid", "spotlight", "backup", "ransomware"] },
  { title: "Arcfra Migration Assessment", description: "Free VMware and Nutanix migration assessment — get a cost comparison and migration plan.", url: "/arcfra-assessment", category: "Services", tags: ["arcfra", "migration", "vmware", "assessment", "nutanix"] },
  // Brands
  { title: "Technology Partners Overview", description: "SE Distribution's authorized distributors (Hytera, Arcfra, ExaGrid) and technology partners.", url: "/brands", category: "Partners", tags: ["brands", "technology partners", "authorized distributor", "hytera", "arcfra", "exagrid", "honeywell", "dell", "synology"] },
  { title: "Hytera Brand Profile", description: "Authorized distributor for Hytera in Malaysia — body cameras, communications, DEMS.", url: "/brands", category: "Partners", tags: ["hytera", "authorized distributor", "body camera", "malaysia"] },
  { title: "Arcfra Brand Profile", description: "Authorized distributor for Arcfra in Malaysia — HCI, all-flash storage, distributed NAS.", url: "/brands", category: "Partners", tags: ["arcfra", "authorized distributor", "hci", "storage", "malaysia"] },
  { title: "ExaGrid Brand Profile", description: "Authorized distributor for ExaGrid in Malaysia — backup appliances, ransomware protection.", url: "/brands", category: "Partners", tags: ["exagrid", "authorized distributor", "backup", "malaysia"] },
  // Industries
  { title: "Industries We Serve", description: "SE Distribution serves law enforcement, government, healthcare, logistics, defence, and more.", url: "/industries", category: "Industries", tags: ["industries", "law enforcement", "government", "healthcare", "logistics", "defence", "manufacturing"] },
  // Company
  { title: "About SE Distribution", description: "SE Distribution Sdn Bhd — authorized distributor and technology partner for enterprise solutions in Malaysia.", url: "/about", category: "Company", tags: ["about", "se distribution", "malaysia", "company", "distributor"] },
  { title: "Contact Us", description: "Get in touch with SE Distribution — sales enquiries, technical support, and solution consultations.", url: "/contact", category: "Company", tags: ["contact", "enquiry", "sales", "support", "phone", "email"] },
  { title: "Case Studies", description: "Real-world deployments — see how SE Distribution has solved challenges for clients across Malaysia.", url: "/case-studies", category: "Company", tags: ["case studies", "deployments", "clients", "projects", "malaysia"] },
];

function scoreResult(result: SearchResult, query: string): number {
  const q = query.toLowerCase().trim();
  const words = q.split(/\s+/);
  let score = 0;
  for (const word of words) {
    if (result.title.toLowerCase().includes(word)) score += 10;
    if (result.description.toLowerCase().includes(word)) score += 5;
    if (result.category.toLowerCase().includes(word)) score += 3;
    for (const tag of result.tags) {
      if (tag.toLowerCase().includes(word)) score += 8;
      if (tag.toLowerCase() === word) score += 4;
    }
  }
  return score;
}

const categoryColors: Record<string, string> = {
  Solutions: "bg-[#1a1a1a] text-white",
  Products: "bg-[#f5f3ee] text-[#1a1a1a]",
  Spotlight: "bg-[#f5f3ee] text-[#1a1a1a]",
  Partners: "bg-[#f5f3ee] text-[#1a1a1a]",
  Industries: "bg-[#f5f3ee] text-[#1a1a1a]",
  Company: "bg-[#f5f3ee] text-[#1a1a1a]",
  Services: "bg-[#f5f3ee] text-[#1a1a1a]",
};

const categoryIcons: Record<string, string> = {
  Solutions: "ri-lightbulb-line",
  Products: "ri-box-3-line",
  Spotlight: "ri-star-line",
  Partners: "ri-shake-hands-line",
  Industries: "ri-building-2-line",
  Company: "ri-building-line",
  Services: "ri-service-line",
};

const allCategories = ["All", "Solutions", "Products", "Spotlight", "Partners", "Industries", "Company", "Services"];

const popularSearches = [
  { label: "Hytera body camera", query: "hytera body camera" },
  { label: "VMware alternative", query: "vmware alternative" },
  { label: "Rugged tablet Malaysia", query: "rugged tablet" },
  { label: "ExaGrid backup", query: "exagrid backup" },
  { label: "GC550", query: "gc550" },
  { label: "Arcfra HCI", query: "arcfra hci" },
  { label: "Law enforcement camera", query: "law enforcement camera" },
  { label: "Ransomware protection", query: "ransomware" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get("q") ?? "";
  const [inputValue, setInputValue] = useState(initialQuery);
  const [activeCategory, setActiveCategory] = useState("All");

  const results = initialQuery.trim()
    ? searchIndex
        .map((r) => ({ ...r, score: scoreResult(r, initialQuery) }))
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score)
    : [];

  const filteredResults = activeCategory === "All"
    ? results
    : results.filter((r) => r.category === activeCategory);

  useSEO({
    title: initialQuery
      ? `Search results for "${initialQuery}" | SE Distribution Malaysia`
      : "Search | SE Distribution Malaysia — Enterprise IT Solutions",
    description: initialQuery
      ? `Found ${results.length} results for "${initialQuery}" on SE Distribution — body cameras, rugged devices, HCI infrastructure, backup solutions in Malaysia.`
      : "Search SE Distribution's full product and solution catalog — Hytera body cameras, Arcfra HCI, ExaGrid backup, rugged computing, and more.",
    canonical: `https://www.sedistribution.com.my/search${initialQuery ? `?q=${encodeURIComponent(initialQuery)}` : ""}`,
  });

  const handleSearch = useCallback(
    (q: string) => {
      if (q.trim()) {
        setSearchParams({ q: q.trim() });
        setActiveCategory("All");
      }
    },
    [setSearchParams]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch(inputValue);
  };

  const handlePopular = (query: string) => {
    setInputValue(query);
    handleSearch(query);
  };

  // Category counts
  const categoryCounts = allCategories.reduce<Record<string, number>>((acc, cat) => {
    acc[cat] = cat === "All" ? results.length : results.filter((r) => r.category === cat).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">

        {/* Search Header */}
        <section className="bg-[#f9f7f4] border-b border-gray-100 py-12 px-6 lg:px-20">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-gray-400 mb-6">
              <Link to="/" className="hover:text-[#1a1a1a] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#1a1a1a]">Search</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6">
              {initialQuery ? (
                <>Search results for <span className="italic">&ldquo;{initialQuery}&rdquo;</span></>
              ) : (
                "Search SE Distribution"
              )}
            </h1>

            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="flex-1 flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3 focus-within:border-[#1a1a1a] transition-colors">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-search-line text-gray-400 text-lg"></i>
                </div>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search products, solutions, brands..."
                  className="flex-1 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none bg-transparent"
                  autoFocus
                />
                {inputValue && (
                  <button
                    onClick={() => { setInputValue(""); setSearchParams({}); }}
                    className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-base"></i>
                  </button>
                )}
              </div>
              <button
                onClick={() => handleSearch(inputValue)}
                className="bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
              >
                Search
              </button>
            </div>

            {/* Popular Searches */}
            {!initialQuery && (
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Popular Searches</p>
                <div className="flex flex-wrap gap-2">
                  {popularSearches.map((s) => (
                    <button
                      key={s.query}
                      onClick={() => handlePopular(s.query)}
                      className="text-xs font-medium bg-white border border-gray-200 text-[#1a1a1a] px-3 py-1.5 rounded-full hover:border-[#1a1a1a] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Result count */}
            {initialQuery && (
              <p className="mt-4 text-sm text-gray-500">
                {results.length > 0
                  ? <><strong className="text-[#1a1a1a]">{results.length}</strong> result{results.length !== 1 ? "s" : ""} found</>
                  : "No results found"}
              </p>
            )}
          </div>
        </section>

        {/* Results */}
        {initialQuery && (
          <section className="py-12 px-6 lg:px-20">
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10">

              {/* Sidebar — Category Filter */}
              <aside className="w-full lg:w-56 flex-shrink-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Filter by Category</p>
                <div className="flex flex-row lg:flex-col gap-2 flex-wrap">
                  {allCategories.map((cat) => {
                    const count = categoryCounts[cat] ?? 0;
                    if (cat !== "All" && count === 0) return null;
                    return (
                      <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          activeCategory === cat
                            ? "bg-[#1a1a1a] text-white"
                            : "bg-[#f9f7f4] text-gray-600 hover:bg-[#f0ede8]"
                        }`}
                      >
                        <span>{cat}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === cat ? "bg-white/20 text-white" : "bg-white text-gray-500"}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </aside>

              {/* Results List */}
              <div className="flex-1 min-w-0">
                {filteredResults.length > 0 ? (
                  <div className="space-y-4">
                    {filteredResults.map((result, i) => (
                      <Link
                        key={`${result.url}-${result.title}-${i}`}
                        to={result.url}
                        className="flex items-start gap-5 p-5 rounded-xl border border-gray-100 hover:border-[#1a1a1a]/20 hover:bg-[#fafaf9] transition-all group"
                      >
                        {/* Icon */}
                        <div className="w-11 h-11 flex items-center justify-center bg-[#f5f3ee] rounded-lg flex-shrink-0 mt-0.5 group-hover:bg-[#1a1a1a] transition-colors">
                          <i className={`${categoryIcons[result.category] ?? "ri-file-line"} text-[#1a1a1a] group-hover:text-white text-lg transition-colors`}></i>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h2 className="text-base font-bold text-[#1a1a1a] group-hover:underline">{result.title}</h2>
                            <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${categoryColors[result.category] ?? "bg-[#f5f3ee] text-[#1a1a1a]"}`}>
                              {result.category}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 leading-relaxed mb-2">{result.description}</p>
                          <div className="flex items-center gap-1.5 text-xs text-gray-400">
                            <i className="ri-link text-xs"></i>
                            <span>sedistribution.com.my{result.url}</span>
                          </div>
                        </div>

                        {/* Arrow */}
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-arrow-right-line text-gray-300 group-hover:text-[#1a1a1a] transition-colors"></i>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="py-20 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-[#f5f3ee] rounded-full mx-auto mb-4">
                      <i className="ri-search-line text-gray-400 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">
                      {activeCategory !== "All" ? `No ${activeCategory} results for "${initialQuery}"` : `No results for "${initialQuery}"`}
                    </h3>
                    <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                      {activeCategory !== "All"
                        ? "Try selecting a different category or broadening your search."
                        : "Try different keywords, or browse our solutions and products below."}
                    </p>
                    {activeCategory !== "All" && (
                      <button
                        onClick={() => setActiveCategory("All")}
                        className="text-sm font-semibold text-[#1a1a1a] underline cursor-pointer mb-6"
                      >
                        Show all categories
                      </button>
                    )}
                    <div className="flex flex-wrap gap-2 justify-center">
                      {popularSearches.map((s) => (
                        <button
                          key={s.query}
                          onClick={() => handlePopular(s.query)}
                          className="text-xs font-medium bg-[#f9f7f4] border border-gray-200 text-[#1a1a1a] px-3 py-1.5 rounded-full hover:border-[#1a1a1a] transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Browse All — shown when no query */}
        {!initialQuery && (
          <section className="py-16 px-6 lg:px-20">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-10">Browse All Content</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { title: "Surveillance & Security", desc: "Body cameras, AI CCTV, evidence management", icon: "ri-camera-3-line", url: "/solutions/surveillance", count: "7 products" },
                  { title: "Rugged Computing", desc: "MIL-SPEC tablets, laptops, handhelds", icon: "ri-smartphone-line", url: "/solutions/rugged-computing", count: "7 devices" },
                  { title: "HCI & Storage", desc: "VMware alternative, backup, NAS", icon: "ri-server-line", url: "/solutions/data-protection", count: "6 products" },
                  { title: "Hytera BWC Spotlight", desc: "Full body-worn camera lineup & DEMS", icon: "ri-vidicon-line", url: "/hytera-bwv", count: "6 cameras" },
                  { title: "Arcfra Platform", desc: "HCI, all-flash storage, distributed NAS", icon: "ri-cpu-line", url: "/arcfra-spotlight", count: "3 products" },
                  { title: "ExaGrid Backup", desc: "Scale-out backup, ransomware protection", icon: "ri-hard-drive-2-line", url: "/exagrid-spotlight", count: "3 appliances" },
                  { title: "Rugged Devices", desc: "Honeywell & Dell Rugged Pro lineup", icon: "ri-macbook-line", url: "/rugged-spotlight", count: "7 devices" },
                  { title: "Technology Partners", desc: "Authorized distributors & tech partners", icon: "ri-shake-hands-line", url: "/brands", count: "6 brands" },
                  { title: "Industries", desc: "Law enforcement, healthcare, logistics & more", icon: "ri-building-2-line", url: "/industries", count: "6 industries" },
                  { title: "Case Studies", desc: "Real deployments across Malaysia", icon: "ri-file-chart-line", url: "/case-studies", count: "Multiple" },
                  { title: "About SE Distribution", desc: "Company background, credentials, team", icon: "ri-information-line", url: "/about", count: "" },
                  { title: "Contact Us", desc: "Sales, support, and consultations", icon: "ri-customer-service-2-line", url: "/contact", count: "" },
                ].map((item) => (
                  <Link
                    key={item.url}
                    to={item.url}
                    className="flex items-start gap-4 p-5 rounded-xl border border-gray-100 hover:border-[#1a1a1a]/20 hover:bg-[#fafaf9] transition-all group cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-[#f5f3ee] rounded-lg flex-shrink-0 group-hover:bg-[#1a1a1a] transition-colors">
                      <i className={`${item.icon} text-[#1a1a1a] group-hover:text-white text-lg transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="font-bold text-[#1a1a1a] text-sm group-hover:underline">{item.title}</h3>
                        {item.count && <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{item.count}</span>}
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="bg-[#f9f7f4] border-t border-gray-100 py-14 px-6 lg:px-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-xl font-bold text-[#1a1a1a] mb-3">Can&apos;t find what you&apos;re looking for?</h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Our solutions team is happy to help. Tell us what you need and we&apos;ll point you in the right direction — or design a custom solution for your requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="bg-[#1a1a1a] text-white text-sm font-semibold px-7 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
              >
                Talk to Our Team <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="tel:+60364127917"
                className="border border-[#1a1a1a] text-[#1a1a1a] text-sm font-medium px-7 py-3 rounded-md hover:bg-[#1a1a1a] hover:text-white transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
              >
                <i className="ri-phone-line"></i>
                +603 6412 7917
              </a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
